import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  trackClickCampaign,
  trackClickCampaignDelete,
  trackClickCampaignRename,
  trackClickCampaignThreeDotsMenu,
} from '../utils/analytics/analytics';
import { trackSentryError } from '../utils/analytics/sentry';
import { colors } from '../utils/colors';
import { ANIMATION_DURATION_MS } from '../utils/const';
import { deleteCampaign, getProduct, updateCampaign } from '../utils/db';
import { parseDomainFromUrl } from '../utils/textUtils';
import { Campaign, LoadingStatus, Product } from '../utils/types';
import {
  IconCampaignDefault,
  IconDelete,
  IconEditText,
  IconError,
  IconMoreHorizontal,
} from './minicomponents/icons';
import { CampaignMetadataSectionComponent } from './minicomponents/minicomponents';
import ConfirmationPopup from './popups/ConfirmationPopup';
import InputPopup from './popups/InputPopup';

const CampaignListItem = ({ campaign }: { campaign: Campaign }) => {
  const [product, setProduct] = useState<Product | null>();
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate(); // Initialize useNavigate
  const location = useLocation(); // Get current location for preserving query params
  const [isIconHovered, setIsIconHovered] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false); // State to control dialog visibility
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false); // State for delete confirmation
  const { t } = useTranslation();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const fetchedProduct = await getProduct(campaign?.product_id!);
        setProduct(fetchedProduct);
      } catch (error) {
        trackSentryError('Error fetching product', {
          productId: campaign?.product_id,
          error,
        });
        console.error('Error fetching product', error);
      } finally {
        setLoading(false);
      }
    };
    if (campaign?.product_id) {
      fetchProduct();
    }
  }, [campaign?.product_id]);

  if (loading) return <></>;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    trackClickCampaignThreeDotsMenu();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  // const handleDownload = async (id?: string) => {
  //   if (id) {
  //     console.log('Download campaign with id:', id);
  //     handleClose();
  //   }
  // };

  const handleRename = async (id?: string) => {
    if (!id) return;
    trackClickCampaignRename();
    setDialogOpen(true); // Open the InputPopup
    handleClose();
  };

  const handleDialogSubmit = async (newName: string) => {
    if (newName) {
      try {
        await updateCampaign(campaign.id!, { name: newName }); // Call updateCampaign
      } catch (error) {
        trackSentryError('Error renaming campaign', {
          campaignId: campaign.id,
          newName,
          error,
        });
        console.error('Error renaming campaign', error);
      }
    }
    setDialogOpen(false); // Close the dialog
  };

  const handleDelete = async (id?: string) => {
    if (id) {
      trackClickCampaignDelete();
      handleClose();
      setDeleteConfirmationOpen(true); // Open the ConfirmationPopup
    }
  };

  const handleIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleClick(event);
  };

  const handleIconMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsIconHovered(true);
  };

  const handleIconMouseLeave = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsIconHovered(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false); // Close the dialog
  };

  const renderMenuIcon = () => {
    if (campaign.status === LoadingStatus.FAILED) {
      return <IconError sx={{ color: 'error.main', width: 24, height: 24 }} />;
    } else if (campaign.status === LoadingStatus.LOADING) {
      return <CircularProgress size={24} sx={{ color: 'text.secondary' }} />;
    } else {
      return <IconMoreHorizontal />;
    }
  };

  return (
    <>
      <Grid size={12} sx={{ width: '100%' }}>
        <Fade in={true} timeout={ANIMATION_DURATION_MS}>
          <Card
            sx={{
              minHeight: '76px',
              alignContent: 'center',
              boxShadow: 'none',
              borderRadius: '6px', // Set border radius to 6px,
              '& .MuiCardContent-root:last-child': {
                pb: 1,
              },
              '&:hover': {
                backgroundColor: isIconHovered ? 'default' : colors.card.hover,
                cursor: isIconHovered ? 'default' : 'pointer',
                '& #campaignText': {
                  color: isIconHovered ? 'inherit' : 'primary.main',
                },
              },
            }}
            onClick={() => {
              trackClickCampaign();
              navigate(`/app/campaigns/${campaign.id}${location.search}`);
            }} // Use navigate for routing
          >
            <CardContent sx={{ paddingX: 2, paddingY: 1 }}>
              <Grid container alignItems={'center'} spacing={2} sx={{ p: 0 }}>
                {/* Left side: Campaign logo and name */}
                <Grid size={5}>
                  <Stack spacing={0.25}>
                    <Stack spacing={1} direction={'row'} alignItems={'center'}>
                      {product?.favicon && (
                        <img
                          src={product?.favicon}
                          alt=""
                          style={{ width: 24, height: 24 }}
                        />
                      )}
                      {!product?.favicon && (
                        <IconCampaignDefault
                          sx={{ width: 24, height: 24, color: '#00134C42' }}
                        />
                      )}
                      <Typography
                        id="campaignText" // Added id for targeting
                        variant="h6"
                        sx={{
                          textDecoration: 'none',
                          fontWeight: 400,
                          color: 'inherit',
                          whiteSpace: 'nowrap', // Prevent line breaks
                          overflow: 'hidden', // Hide overflow
                          textOverflow: 'ellipsis', // Add ellipsis for overflow
                        }}
                      >
                        {parseDomainFromUrl(product?.url) ? (
                          <>
                            <strong>{parseDomainFromUrl(product?.url)}</strong>
                            &nbsp;&nbsp;|&nbsp;&nbsp;
                            {campaign.name}
                          </>
                        ) : (
                          campaign.name
                        )}
                      </Typography>
                    </Stack>
                    {product?.description && (
                      <Typography
                        color={'text.secondary'}
                        noWrap
                        sx={{ fontSize: '90%' }}
                      >
                        {product?.description}
                      </Typography>
                    )}
                  </Stack>
                </Grid>

                {/* Right side: Campaign details */}
                <CampaignMetadataSectionComponent
                  createdAt={campaign.created_at}
                  language={campaign.language}
                  creativity={campaign.creativity}
                  isEvenSpacing={true}
                  sx={{ justifyContent: 'center', pl: 4 }}
                />

                {/* Menu icon */}
                <Grid>
                  <IconButton
                    aria-label="more"
                    onClick={handleIconClick}
                    onMouseEnter={handleIconMouseEnter}
                    onMouseLeave={handleIconMouseLeave}
                    disabled={
                      campaign.status === LoadingStatus.LOADING ||
                      campaign.status === LoadingStatus.FAILED
                    }
                  >
                    {renderMenuIcon()}
                  </IconButton>
                  <Menu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* <MenuItem
                      onClick={() => {
                        handleDownload(campaign.id);
                      }}>
                      Download
                    </MenuItem> */}
                    <MenuItem
                      onClick={() => {
                        handleRename(campaign.id);
                      }}
                    >
                      <ListItemIcon>
                        <IconEditText />
                      </ListItemIcon>
                      {t('app.buttons.rename')}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        handleDelete(campaign.id);
                      }}
                    >
                      <ListItemIcon>
                        <IconDelete />
                      </ListItemIcon>

                      {t('app.buttons.delete')}
                    </MenuItem>
                  </Menu>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      </Grid>

      {/* InputPopup for renaming campaign */}
      <InputPopup
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        buttonText={t('app.buttons.rename')}
        dialogTitle={t('app.popups.rename.title')}
        dialogText={t('app.popups.rename.text')}
        inputValue={campaign.name || t('app.popups.rename.defaultName')}
      />

      {/* ConfirmationPopup for delete confirmation */}
      <ConfirmationPopup
        open={isDeleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onSubmit={async () => {
          try {
            await deleteCampaign(campaign.id!);
          } catch (error) {
            trackSentryError('Error deleting campaign', {
              campaignId: campaign.id,
              error,
            });
            console.error('Error deleting campaign', error);
          }
          setDeleteConfirmationOpen(false);
        }}
        confirmButtonText={t('app.buttons.delete')}
        message={t('app.popups.delete.text')}
        title={t('app.popups.delete.title')}
        disclaimer={t('app.popups.disclaimers.cannotBeUndone')}
        confirmButtonColor="error.main"
      />
    </>
  );
};

export default CampaignListItem;
